import type { ColDef } from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../../hooks/useFetchForecastMetaData";
import type { ForecastTableControlValues } from "../../../tableControls/forecastTableControlValues";
import {
  type CubeWindow,
  isForecastCellEditable,
  isIndexForecastCellEditable
} from "../../utils/columnUtils";

/**
 * Returns the appropriate `editable` value for time period columns
 * based on the current planBy and intake modes.
 *
 * ``editableProducer`` is required — callers must explicitly pass the
 * producer whose cells should be editable (``"user"`` or ``"budget"``),
 * or ``undefined`` to disable edits (e.g. a frozen budget cube).
 * Silently defaulting would let a missed parameter bias the table
 * toward forecast-mode editability on a budget page.
 */
/** Options bag for {@link getEditableByPlanBy}. Grouped to keep the
 * function's parameter count under the lint rule's ceiling while
 * letting decision-N additive props (editable row predicate) sit
 * alongside the existing fields. */
export interface EditableByPlanByOptions {
  planBy: ForecastTableControlValues["planBy"];
  planningYear: string;
  metaData: ForecastMetaDataType;
  intake: ForecastTableControlValues["intake"] | undefined;
  editableProducer: string | undefined;
  /** Decision N: per-row editability narrowing (e.g. NIS3-only). */
  editableRowPredicate?: (row: { metric_name: string }) => boolean;
  /** Decision N: caller-supplied metric whitelist (range plan unlocks NIS3). */
  editableMetricsOverride?: ReadonlySet<string>;
  /** Decision N: per-cell time-window override (range-plan future weeks). */
  editableTimeWindowPredicate?: (cell: {
    metric_name: string;
    producer_type: string;
    fiscal_year_name: string;
    colId: string;
  }) => boolean;
  /** The save cube's week window; cells outside it are read-only. */
  cubeWindow?: CubeWindow;
}

export function getEditableByPlanBy({
  planBy,
  planningYear,
  metaData,
  intake,
  editableProducer,
  editableRowPredicate,
  editableMetricsOverride,
  editableTimeWindowPredicate,
  cubeWindow
}: EditableByPlanByOptions): ColDef["editable"] {
  switch (planBy) {
    case "SH%":
      return false;
    case "IDX":
    case "+/- Var":
      return (params) =>
        isIndexForecastCellEditable(
          params,
          {
            editable_producer: editableProducer,
            planning_year: planningYear,
            intake,
            editable_row_predicate: editableRowPredicate,
            editable_metrics_override: editableMetricsOverride,
            editable_time_window_predicate: editableTimeWindowPredicate,
            cube_window: cubeWindow
          },
          metaData
        );
    case "ABS":
    default:
      return (params) =>
        isForecastCellEditable(
          params,
          {
            editable_producer: editableProducer,
            intake,
            editable_row_predicate: editableRowPredicate,
            editable_metrics_override: editableMetricsOverride,
            editable_time_window_predicate: editableTimeWindowPredicate,
            cube_window: cubeWindow
          },
          metaData
        );
  }
}
